import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BenchmarkDataLookupService,
  BenchmarkDataPrimaryLookupService,
  BmarkFixingData,
  BmarkFixingType,
  CcyLookupService,
  CcyPromiseLookupService,
  HedgeOrderDataPrimaryLookupService,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  TraderGroupLookupService,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ActivatedRoute } from "@angular/router";
import { AmountViewType } from "app/bfx-bmark-summary/bmark-summary-settings-menu.component";
import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import { BenchmarkPositionLookupService } from "shared/cubiq-services/lookup/model/impl/BenchmarkPositionLookupService";
import {
  BLOTTER_RELOAD_TYPE,
  RiskModelDataListener,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { BaseBMarkRiskTableDefinitionFactory } from "shared/cubiq-services/model/BaseBmarkRiskTableDefinitionFactory";
import { BmarkRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkRiskDisplay.model";
import {
  BmarkBaseRiskDisplayModel,
  BmarkRiskPricingErrorDisplayModel,
  BmarkRiskTGDisplayModel,
} from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { BfxBaseBlotterService } from "../../../../bfx-base-blotter/bfx-base-blotter.service";
import { BmarkClientOrdersDataService } from "../../client-orders/services/bmark-client-orders-data.service";

type TGNetRiskType = {
  /** True if any orders active - if false, all orders are unfilled and terminal  */
  hasActiveOrders: boolean;

  tgId: CubiqTS.Long;
  tgName: string;

  getNumOrders(): number;
  netBaseAmt: CubiqTS.BigNumber;
  netFillAmt: CubiqTS.BigNumber;
};

/**
 * Interfaces for aggregate risk for a pair, used for common processing of both spot and fwd risk
 * - Maps to SpotRiskPair and FwdRiskPair
 */
type RiskPairType = {
  internalTotal: TGNetRiskType;
  externalTotal: TGNetRiskType;
  total: CubiqTS.BigNumber;
  baseCCY: string;
  nonBaseCCY: string;
  tgTotals: Map<string, TGNetRiskType>;
  errors: RiskPairError;
};

type RiskPairError = {
  orderCount: number;
  tgTotals: Map<string, TGNetRiskType>;
};

/**
 * Abstract base service providing aggregate risk data model for both spot and fwd risk blotters
 */
export abstract class BmarkBaseRiskDataService
  extends BfxBaseBlotterService
  implements RiskModelDataListener
{
  protected activeFixingId: string;
  private activeFixingType: BmarkFixingType = null;
  private blotterMode: BmarkClientOrdersBlotterMode;
  public baseCcyLookup: CcyLookupService;
  public baseTraderGroupLookup: TraderGroupLookupService;
  //
  protected materialCcyDataset: Array<string> = new Array<string>();

  private amtViewType: AmountViewType = AmountViewType.MILLIONS;

  constructor(
    public dataService: BenchmarkDataPrimaryLookupService,
    public hedgingDataService: HedgeOrderDataPrimaryLookupService,
    public tableDefinitionService: BaseBMarkRiskTableDefinitionFactory,
    protected bmarkManager: BmarkFixingsManagerService,
    protected profileActionService: ProfilePromiseActionService,
    protected clientOrdersDataService: BmarkClientOrdersDataService,
    protected traderGroupLookup: TraderGroupPromiseLookupService,
    protected ccyService: CcyLookupService,
    private _traderGroupLookup: TraderGroupLookupService,
    protected _ccyLookupService: CcyPromiseLookupService,
    protected cmdLine: ActivatedRoute,
    protected positionService: BenchmarkPositionLookupService,
    protected sysConfigLookup: SystemConfigPromiseService,
  ) {
    super(dataService, tableDefinitionService);
    this.baseTraderGroupLookup = this.traderGroupLookup.getBase();
    this.baseCcyLookup = this._ccyLookupService.getBase();
    this.initialize();
  }

  protected abstract initialize();

  protected onFixingModelUpdate(BenchmarkFixingImpl): void {
    this.tableDataReload(BLOTTER_RELOAD_TYPE.INIT, true);
  }

  protected onPositionModeChange(): void {
    this.tableDataReload(BLOTTER_RELOAD_TYPE.INIT, true);
  }

  protected createListOfCcyMaterials(): void {
    let allCcy = this.baseCcyLookup.getAll();
    let materialCcy = allCcy.filter((item: CubiqTS.CurrencyImpl) => {
      return item.positionUnitId === CubiqTS.PositionUnit.Oz;
    });

    if (Array.isArray(materialCcy) && materialCcy.length > 0) {
      this.materialCcyDataset = materialCcy.map((item) => item.id);
    }
  }

  /*************************************/
  /* RiskModel - Event handlers */
  public onBlotterReload(reason: BLOTTER_RELOAD_TYPE): void {
    this.tableDataReload();
  }
  public onUpdatePairs(updateCcyPairs: Set<string>): void {
    this.tableDataUpdateRows(Array.from(updateCcyPairs), false);
  }
  public onRemovePairs(removeCcyPairs: Set<string>) {
    this.tableDataReload();
  }

  /*************************************/
  /* Event handlers */

  protected onFixingManagerEvent(fixingData: BmarkFixingData) {
    //logger.out.info(`BMARK: SpotRisk: Received BmarkFixingManagerEvent [${fixingData instanceof BmarkFixingData ? fixingData.id: 'NULL'}] => %O`, fixingData);
    let fixingChanged: boolean = false;
    if (fixingData instanceof BmarkFixingData) {
      fixingChanged =
        this.activeFixingId !== fixingData.id || this.activeFixingType !== fixingData.type;
      this.activeFixingId = fixingData.id;
      this.activeFixingType = fixingData.type;
    } else {
      fixingChanged = this.activeFixingId !== "";
      this.activeFixingId = "";
      this.activeFixingType = null;
    }

    if (fixingChanged) {
      this.tableDataReload();
    }
  }

  /*************************************/
  /* TABLE EVENTS */
  public initData(): void {
    this.tableDataReload();
  }

  protected abstract tableDataReload(reason?: BLOTTER_RELOAD_TYPE, forceRepaint?: boolean): void;

  protected tableDataDeleteRows(ccyPairIds: Array<string>): void {
    if (Array.isArray(ccyPairIds) && ccyPairIds.length > 0) {
      let rowsToDelete = this.getRowData().filter((item: BmarkBaseRiskDisplayModel) =>
        ccyPairIds.includes(item.ccyPair),
      );
      if (Array.isArray(rowsToDelete) && rowsToDelete.length > 0) {
        for (let row of rowsToDelete) {
          delete this._rawData[row.id];
          this.deleteItemSubject.next(row.id);
        }
      }
    }
  }

  protected abstract tableDataUpdateRows(ccyPairIds: Array<string>, forceRepaint?: boolean): void;

  public getBlotterMode(): BmarkClientOrdersBlotterMode {
    return this.blotterMode;
  }

  /*************************************/
  /* DATA */
  public setBlotterMode(tableViewType): void {
    if (tableViewType in BmarkClientOrdersBlotterMode) {
      if (tableViewType !== this.blotterMode) {
        this.blotterMode = tableViewType;
        this.tableDataReload(BLOTTER_RELOAD_TYPE.VIEW_CHANGE);
      }
    }
  }

  public getItemCount(): number {
    return this.getRowData().length;
  }

  public getSelectedFixingData(): BmarkFixingData {
    let bmarkFixingLookupService: BenchmarkDataLookupService = this.dataService.getBase();
    let activeFixingData = bmarkFixingLookupService.getActiveFixingData(this.activeFixingId);

    if (activeFixingData == null) {
      let hedgeFixingLookupService = this.hedgingDataService?.getBase();
      if (hedgeFixingLookupService != null) {
        activeFixingData = hedgeFixingLookupService.getActiveFixingData(this.activeFixingId);
      }
    }

    return activeFixingData;
  }
  protected abstract getAlertThresholds(fixingId: string): Map<string, CubiqTS.BigNumber>;
  protected abstract getWarningThresholds(fixingId: string): Map<string, CubiqTS.BigNumber>;

  protected abstract createDisplayModel(key: string, value: any): BmarkRiskDisplayModel;

  public processRiskData(riskData: Map<string, RiskPairType>): Array<BmarkRiskDisplayModel> {
    let result = new Array<BmarkRiskDisplayModel>();

    for (const key of riskData.keys()) {
      let value: RiskPairType = riskData.get(key);
      let row = this.createDisplayModel(this.activeFixingId + key, value);
      row.ccyPair = value.baseCCY + value.nonBaseCCY;
      row.instrument = value.baseCCY + "/" + value.nonBaseCCY;
      row.baseCcy = value.baseCCY;
      row.nonBaseCcy = value.nonBaseCCY;
      row.totalInternal = value.internalTotal.netBaseAmt;
      row.totalExternal = value.externalTotal.netBaseAmt;
      row.total = value.total;

      row.totalInternalOrdersCount = value.internalTotal.getNumOrders();
      row.totalExternalOrdersCount = value.externalTotal.getNumOrders();

      let pair: string = row.baseCcy + row.nonBaseCcy;

      let alertThresholds: Map<string, CubiqTS.BigNumber> = this.getAlertThresholds(
        this.activeFixingId,
      );
      row.isPositionAlert = this.doesPositionBreachThreshold(
        alertThresholds,
        pair,
        row.totalInternal,
      );
      if (!row.isPositionAlert) {
        let warnThresholds: Map<string, CubiqTS.BigNumber> = this.getWarningThresholds(
          this.activeFixingId,
        );
        row.isPositionWarning = this.doesPositionBreachThreshold(
          warnThresholds,
          pair,
          row.totalInternal,
        );
      }

      // meta-data
      row.isMaterial =
        Array.isArray(this.materialCcyDataset) && this.materialCcyDataset.includes(value.baseCCY);
      row.fullPrecision = this.getCurrencyPrecision(value.baseCCY);
      if (row.isMaterial) {
        row.notation = CubiqTS.PositionUnit.Oz;
        row.precision = this.amtViewType === AmountViewType.MILLIONS ? 0 : row.fullPrecision;
      } else {
        switch (this.amtViewType) {
          case AmountViewType.MILLIONS: {
            row.notation = CubiqTS.PositionUnit.Million;
            row.precision = 2;
            break;
          }
          case AmountViewType.NOTIONAL:
          default: {
            row.notation = CubiqTS.PositionUnit.Oz;
            row.precision = row.fullPrecision;
            break;
          }
        }
      }

      // Trader Groups DATA
      if (value.tgTotals instanceof Map && value.tgTotals.size > 0) {
        for (const tgKey of value.tgTotals.keys()) {
          let tgValue: TGNetRiskType = value.tgTotals.get(tgKey);
          let targetTradingGroup: CubiqTS.TradingGroupImpl = this._traderGroupLookup.getItem(tgKey);
          let tg = new BmarkRiskTGDisplayModel();
          tg.tgId = CubiqTS.Long.fromString(tgKey);
          tg.totalAmount = tgValue.netBaseAmt;
          tg.fillAmount = tgValue.netFillAmt;
          tg.orderCount = tgValue.getNumOrders();
          tg.hasActiveOrders = tgValue.hasActiveOrders;
          tg.tgName =
            targetTradingGroup instanceof CubiqTS.TradingGroupImpl
              ? targetTradingGroup.groupCode
              : null;
          tg.isBmarkExternal =
            targetTradingGroup instanceof CubiqTS.TradingGroupImpl &&
            typeof targetTradingGroup.isAutoFilledFg === "boolean" &&
            targetTradingGroup.isAutoFilledFg;
          row.tgData.push(tg);
        }
      }
      // Warning DATA
      // need to see a difference to redraw - even when unset - so give it a value - but have it empty
      row.pricingError = new BmarkRiskPricingErrorDisplayModel();
      if (
        value.errors &&
        value.errors.orderCount > 0 &&
        value.errors.tgTotals &&
        value.errors.tgTotals.size > 0
      ) {
        row.pricingError.orderCount = value.errors.orderCount;
        for (const tgValue of value.errors.tgTotals.values()) {
          let tg = new BmarkRiskTGDisplayModel();
          tg.tgId = tgValue.tgId;
          tg.tgName = tgValue.tgName;
          tg.orderCount = tgValue.getNumOrders();
          tg.hasActiveOrders = tgValue.hasActiveOrders;
          tg.totalAmount = tgValue.netBaseAmt;
          tg.fillAmount = tgValue.netFillAmt;
          row.pricingError.totalAmount = row.pricingError.totalAmount.plus(tg.totalAmount);
          row.pricingError.tgData.push(tg);
        }
      }
      result.push(row);
    }
    return result;
  }

  protected doesPositionBreachThreshold(
    thresholds: Map<string, CubiqTS.BigNumber>,
    key: string,
    amount: CubiqTS.BigNumber,
  ): boolean {
    let ret: boolean = false;
    if (thresholds && amount && key) {
      let threshold: CubiqTS.BigNumber = thresholds.get(key);
      if (threshold && amount.abs().isGreaterThanOrEqualTo(threshold)) {
        ret = true;
      }
    }
    return ret;
  }

  protected getCurrencyPrecision(ccy: string): number {
    let ret: number = 2; // valid default?
    if (this.baseCcyLookup instanceof CcyLookupService) {
      let ccyDef: CubiqTS.CurrencyImpl = this.baseCcyLookup.getItem(ccy);
      if (ccyDef instanceof CubiqTS.CurrencyImpl) {
        ret = ccyDef.minorUnitsQty.toInt();
      }
    }
    return ret;
  }

  public setAmountViewType(amtViewType: AmountViewType) {
    if (amtViewType != this.amtViewType) {
      this.amtViewType = amtViewType;
      this.tableDataReload(BLOTTER_RELOAD_TYPE.VIEW_CHANGE, true);
    }
  }

  public getAmountViewType(): AmountViewType {
    return this.amtViewType;
  }

  public isPositionInManualHedgeMode(positionId: string): boolean {
    return (
      this.blotterMode == BmarkClientOrdersBlotterMode.RISK &&
      this.positionService.getHedgingMode(positionId) === CubiqTS.HedgingModeType.MANUAL
    );
  }

  public getHedgingModeChangeUserId(positionId: string): CubiqTS.Long {
    return this.positionService.getHedgingModeChangeUserId(positionId);
  }

  public getActiveFixingType(): BmarkFixingType {
    return this.activeFixingType;
  }
}
